import { webinarInputSchema, type Webinar } from '@aulaviva/shared';
import { Alert, Button, Checkbox, NativeSelect, TextArea, TextField, cx, useToast } from '@aulaviva/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Laptop, Radio } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { z } from 'zod';
import { useAggiornaWebinar, useCreaWebinar, usePercorsiAdmin, useUtentiAdmin, useWebinarAdminDettaglio } from '@/api/admin';
import { ErroreRichiesta } from '@/api/client';
import { inputLocaleAIso, isoAInputLocale, minutiTra } from '@/lib/date';
import { admin, comuni } from '@/testi';
import { IntestazioneAdmin, LinkIndietro } from './comuni';

type Input = z.input<typeof webinarInputSchema>;
type Output = z.output<typeof webinarInputSchema>;

export function WebinarForm() {
  const { id } = useParams();
  const esistente = useWebinarAdminDettaglio(id ?? '');
  if (id && esistente.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (id && esistente.isError) return <Alert variant="error" title={esistente.error.message} />;
  return <Modulo key={id ?? 'nuovo'} id={id} iniziale={esistente.data} />;
}

/** Campi orario in Europe/Rome: nel modulo sono stringhe "datetime-local", verso l'API diventano ISO UTC. */
interface Locale {
  inizio: string;
  fine: string;
}

function Modulo({ id, iniziale }: { id?: string; iniziale?: Webinar }) {
  const t = admin.webinar.form;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const percorsi = usePercorsiAdmin();
  const relatori = useUtentiAdmin({ ruolo: 'relatore', perPagina: 200 });
  const crea = useCreaWebinar();
  const aggiorna = useAggiornaWebinar(id ?? '');
  const [errore, setErrore] = useState<string | null>(null);
  const [locale, setLocale] = useState<Locale>({ inizio: isoAInputLocale(iniziale?.inizioPrevisto), fine: isoAInputLocale(iniziale?.finePrevista) });
  const [durataToccata, setDurataToccata] = useState(Boolean(iniziale));

  const { register, control, handleSubmit, setError, setValue, watch, formState } = useForm<Input, unknown, Output>({
    resolver: zodResolver(webinarInputSchema),
    defaultValues: iniziale
      ? {
          percorsoId: iniziale.percorsoId,
          titolo: iniziale.titolo,
          descrizione: iniziale.descrizione ?? '',
          inizioPrevisto: iniziale.inizioPrevisto,
          finePrevista: iniziale.finePrevista,
          durataUfficialeMin: iniziale.durataUfficialeMin,
          modalitaIngresso: iniziale.modalitaIngresso,
          sogliaPresenzaPct: iniziale.sogliaPresenzaPct,
          testDurataMin: iniziale.testDurataMin,
          testFinestraOre: iniziale.testFinestraOre,
          relatoriIds: iniziale.relatori.map((r) => r.id),
        }
      : {
          percorsoId: params.get('percorso') ?? '',
          titolo: '',
          descrizione: '',
          inizioPrevisto: '',
          finePrevista: '',
          durataUfficialeMin: 180,
          modalitaIngresso: 'regia',
          sogliaPresenzaPct: 75,
          testDurataMin: 10,
          testFinestraOre: 2,
          relatoriIds: [],
        },
  });

  // orari locali → ISO nel modulo; durata precompilata finché l'utente non la tocca
  useEffect(() => {
    const inizio = inputLocaleAIso(locale.inizio);
    const fine = inputLocaleAIso(locale.fine);
    setValue('inizioPrevisto', inizio, { shouldValidate: formState.isSubmitted });
    setValue('finePrevista', fine, { shouldValidate: formState.isSubmitted });
    if (!durataToccata && inizio && fine) setValue('durataUfficialeMin', minutiTra(inizio, fine));
  }, [locale, durataToccata, setValue, formState.isSubmitted]);

  async function invia(dati: Output) {
    setErrore(null);
    try {
      const salvato = id ? await aggiorna.mutateAsync(dati) : await crea.mutateAsync(dati);
      toast.mostra({ variant: 'success', title: id ? admin.comuni.salvato : t.creato });
      navigate(`/admin/webinar/${salvato.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ErroreRichiesta) {
        for (const [campo, m] of Object.entries(e.dettagli ?? {})) setError(campo as keyof Input, { message: m[0] });
        setErrore(e.message);
      } else setErrore(comuni.erroreGenerico);
    }
  }

  const err = formState.errors;
  const modalita = watch('modalitaIngresso');
  const opzioniPercorso = (percorsi.data ?? []).filter((p) => p.stato !== 'chiuso').map((p) => ({ value: p.id, label: `${p.titolo} (${p.anno})` }));
  const elencoRelatori = relatori.data?.elementi ?? [];

  return (
    <>
      <IntestazioneAdmin titolo={id ? t.titoloModifica : t.titoloNuovo} sopra={<LinkIndietro to={id ? `/admin/webinar/${id}` : '/admin/webinar'} testo={admin.webinar.titolo} />} />
      <form onSubmit={handleSubmit(invia)} noValidate className="flex flex-col gap-8 max-w-[720px]">
        {errore && <Alert variant="error" title={errore} />}
        <NativeSelect label={t.percorso} options={opzioniPercorso} placeholder="—" disabled={Boolean(id)} error={err.percorsoId?.message} {...register('percorsoId')} />
        <TextField label={t.titolo} error={err.titolo?.message} {...register('titolo')} />
        <TextArea label={t.descrizione} error={err.descrizione?.message} {...register('descrizione')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <TextField label={t.inizio} type="datetime-local" hint={t.orariAiuto} value={locale.inizio} onChange={(e) => setLocale((l) => ({ ...l, inizio: e.target.value }))} error={err.inizioPrevisto?.message} />
          <TextField label={t.fine} type="datetime-local" hint={t.orariAiuto} value={locale.fine} onChange={(e) => setLocale((l) => ({ ...l, fine: e.target.value }))} error={err.finePrevista?.message} />
        </div>
        <TextField
          label={t.durata}
          type="number"
          inputMode="numeric"
          hint={t.durataAiuto}
          className="max-w-[240px]"
          error={err.durataUfficialeMin?.message}
          {...register('durataUfficialeMin', { valueAsNumber: true, onChange: () => setDurataToccata(true) })}
        />

        <fieldset className="flex flex-col gap-3">
          <legend className="t-label mb-2">{t.relatori}</legend>
          <p className="t-body-small text-ink-muted">{t.relatoriAiuto}</p>
          {elencoRelatori.length === 0 && !relatori.isPending && <p className="t-body text-ink-muted">{t.nessunRelatore}</p>}
          <Controller
            control={control}
            name="relatoriIds"
            render={({ field }) => (
              <div className="flex flex-col">
                {elencoRelatori.map((r) => {
                  const selezionati = field.value ?? [];
                  const attivo = selezionati.includes(r.id);
                  return (
                    <Checkbox
                      key={r.id}
                      checked={attivo}
                      onCheckedChange={(v) => field.onChange(v ? [...selezionati, r.id] : selezionati.filter((x) => x !== r.id))}
                      label={`${r.cognome} ${r.nome}`}
                      hint={r.email}
                    />
                  );
                })}
              </div>
            )}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-3">
          <legend className="t-label mb-2">{t.modalita}</legend>
          <Controller
            control={control}
            name="modalitaIngresso"
            render={({ field }) => (
              <div role="radiogroup" aria-label={t.modalita} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(
                  [
                    { v: 'regia', titolo: t.modalitaRegia, testo: t.modalitaRegiaTesto, icona: Radio },
                    { v: 'relatore', titolo: t.modalitaRelatore, testo: t.modalitaRelatoreTesto, icona: Laptop },
                  ] as const
                ).map(({ v, titolo, testo, icona: Icona }) => {
                  const attiva = (field.value ?? 'regia') === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={attiva}
                      onClick={() => field.onChange(v)}
                      className={cx('flex items-start gap-3 text-left rounded-md border p-4 bg-surface', attiva ? 'border-accent ring-2 ring-accent' : 'border-border-strong hover:bg-surface-sunken')}
                    >
                      <Icona aria-hidden size={24} strokeWidth={1.75} className="shrink-0 mt-0.5" />
                      <span className="flex flex-col gap-1">
                        <span className="t-title-3">{titolo}</span>
                        <span className="t-body-small text-ink-muted">{testo}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          />
          {modalita === 'relatore' && <Alert variant="warning" title={t.modalitaRelatoreTesto} />}
        </fieldset>

        <fieldset className="flex flex-col gap-6">
          <legend className="t-label mb-2">{t.soglie}</legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <TextField label={t.soglia} type="number" inputMode="numeric" hint={t.sogliaAiuto} error={err.sogliaPresenzaPct?.message} {...register('sogliaPresenzaPct', { valueAsNumber: true })} />
            <TextField label={t.testDurata} type="number" inputMode="numeric" error={err.testDurataMin?.message} {...register('testDurataMin', { valueAsNumber: true })} />
            <TextField label={t.testFinestra} type="number" inputMode="numeric" error={err.testFinestraOre?.message} {...register('testFinestraOre', { valueAsNumber: true })} />
          </div>
        </fieldset>

        <div className="flex gap-3">
          <Button type="submit" variant="primary" loading={crea.isPending || aggiorna.isPending} loadingText={admin.comuni.inCorso}>
            {id ? admin.comuni.salva : t.crea}
          </Button>
          <Button onClick={() => navigate(-1)}>{admin.comuni.annulla}</Button>
        </div>
      </form>
    </>
  );
}
